import { AxiosError, AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import { appURLS } from 'appRoot/app.constants';
import AdminService from 'external-services/diary-admin-service/admin.service';

const NS = 'GridSyncService'; // jshint ignore:line
const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

// Exponential backoff delays (ms) for transient failures: 3 retries after the
// initial attempt. Edit this array to change the count or timing.
const RETRY_DELAYS_MS = [500, 1000, 2000];

/**
 * Grid Sync API notifications (CLI-165).
 *
 * Some entities (block outs, schedules, sections, layouts, events) are updated
 * directly via the Diary SPA against CouchDB, so the Reservation API micro
 * services never find out they changed. After a successful CouchDB write we
 * POST the entity id to the Legacy API's Grid Sync endpoints so it can notify
 * the Reservation API.
 *
 * Endpoints (relative to the Legacy API server):
 *   /sync/venues/{venueId}/{action}   POST { entityId }
 *
 * Fire-and-forget: the CouchDB write is the source of truth. A failed
 * notification is logged but never blocks or fails the user's action.
 */
class GridSyncService {
  private notify(action: string, entityId: string): void {
    if (!entityId) {
      console.warn(NS, 'skipping grid sync notification, missing entityId', action);
      return;
    }

    const venue = AdminService.getVenue();
    if (!venue || !venue.id) {
      console.warn(NS, 'skipping grid sync notification, missing venueId', action);
      return;
    }
    const venueId = venue.id;
    // baseAPI may or may not have a trailing slash depending on the environment
    // config, so strip it to avoid a double slash before "/sync".
    const apiBase = appURLS.API.replace(/\/+$/, '');

    this.post(`${apiBase}/sync/venues/${venueId}/${action}`, action, entityId, 0);
  }

  /**
   * POSTs the notification, retrying transient failures with exponential
   * backoff (see RETRY_DELAYS_MS). Fire-and-forget: a final failure is logged
   * but never disrupts the user's save (CLI-165).
   */
  private post(url: string, action: string, entityId: string, attempt: number): void {
    api.post(url, { entityId }).catch((error: AxiosError) => {
      const canRetry = attempt < RETRY_DELAYS_MS.length && this.isRetryable(error);
      if (!canRetry) {
        console.error(NS, `grid sync notification failed: ${action} (${entityId})`, error);
        return;
      }

      const delay = RETRY_DELAYS_MS[attempt];
      console.warn(
        NS,
        `grid sync notification failed, retrying in ${delay}ms ` +
          `(attempt ${attempt + 1}/${RETRY_DELAYS_MS.length}): ${action} (${entityId})`
      );
      setTimeout(() => this.post(url, action, entityId, attempt + 1), delay);
    });
  }

  /**
   * Only retry on transient failures: network/timeout errors (no response) or
   * server errors (5xx). 4xx (auth, ring-fencing, not found) won't succeed on
   * retry, so give up immediately.
   */
  private isRetryable(error: AxiosError): boolean {
    const status = error?.response?.status;
    return status === undefined || status >= 500;
  }

  public blockOutCreated(entityId: string): void {
    this.notify('block-out-created', entityId);
  }

  public blockOutEdited(entityId: string): void {
    this.notify('block-out-edited', entityId);
  }

  public blockOutDeleted(entityId: string): void {
    this.notify('block-out-deleted', entityId);
  }

  public scheduleEdited(entityId: string): void {
    this.notify('schedule-edited', entityId);
  }

  public sectionEdited(entityId: string): void {
    this.notify('section-edited', entityId);
  }

  public layoutCreated(entityId: string): void {
    this.notify('layout-created', entityId);
  }

  public layoutEdited(entityId: string): void {
    this.notify('layout-edited', entityId);
  }

  public layoutDeleted(entityId: string): void {
    this.notify('layout-deleted', entityId);
  }

  public eventCreated(entityId: string): void {
    this.notify('event-created', entityId);
  }

  public eventEdited(entityId: string): void {
    this.notify('event-edited', entityId);
  }

  public eventDeleted(entityId: string): void {
    this.notify('event-deleted', entityId);
  }
}

const instance = new GridSyncService();
export default instance as GridSyncService;
